import Honeybadger from "@honeybadger-io/js";
import { CommunityId, FileIcon } from "@sp-crm/core";
import { MultipleFileUpload } from "components/shared/multiple-file-upload";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { SecondaryButton } from "components/ui/secondary-button";
import * as React from "react";
import { useCallback } from "react";
import { useRegionId } from "store/selectors/hooks";

interface CommunityIntakeUploadPanelProps {
    communityId: CommunityId;
    onDismiss: () => void;
}

export const CommunityIntakeUploadPanel: React.FC<CommunityIntakeUploadPanelProps> = (
    props,
): JSX.Element => {
    const { onDismiss, communityId } = props;
    const [hasUploadedFiles, setHasUploadedFiles] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const regionId = useRegionId();

    const onAddNewFiles = useCallback(
        async (files: File[]) => {
            try {
                const formData = new FormData();
                files.forEach(file => formData.append("files", file));
                formData.append("regionId", regionId);

                const result = await fetch(
                    `/api/communities/${communityId}/intake-file`,
                    {
                        method: "POST",
                        body: formData,
                        credentials: "include",
                    },
                );

                if (!result.ok) {
                    const body = await result.json();
                    throw new Error(
                        `Failed to upload files. Please contact support, or try another file. Correlation ID: ${body.correlationId}`,
                    );
                }

                setHasUploadedFiles(true);
            } catch (error) {
                Honeybadger.notify(error);
                setError(error.message);
            }
        },
        [communityId, regionId],
    );

    return (
        <Panel
            type={PanelType.largeFixed}
            onDismiss={onDismiss}
            isOpen={true}
            headerText="Upload Community Intake Form">
            <div>
                {error && <InlineBanner type="error">{error}</InlineBanner>}
                {!hasUploadedFiles ? (
                    <MultipleFileUpload
                        onAddNewFiles={onAddNewFiles}
                        title="Upload Community Intake PDF"
                        imageRenderInfo={{
                            fileGroupIcon: "02_IntakeComplete" as FileIcon,
                            fileGroupHeading: "Community Intake Files",
                        }}
                    />
                ) : (
                    <div className="flex-row-center">
                        <p className="mb-2">
                            The file has been uploaded successfully. Please review the
                            community data to ensure that it was imported correctly.
                        </p>
                        <SecondaryButton onClick={onDismiss}>Close</SecondaryButton>
                    </div>
                )}
            </div>
        </Panel>
    );
};
